import firebase from '../../../firebase/compat';
import { useContext, useState } from 'react';
import { v4 as uuid } from 'uuid';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useFirestoreQuery } from '../../../firebase/useFirestore';
import { db } from '../../../firebase/config';
import { client } from '../../../hooks/Client';
import usePermissions from '../../../hooks/usePermissions';
import { Auth } from '../../../StateManagment/Auth';
import ReadOnlyGuard from '../../common/ReadOnlyGuard';
import DeleteModal from '../../common/DeleteModal';
import Table from '../../common/table/Table.jsx';
import TableCell from '../../common/table/TableCell.jsx';
import softDeleteDoc from '../../../helpers/softDeleteDoc';
import formatEuro from '../../../helpers/formatEuro';
import formatDateKey from '../../../helpers/billing/formatDateKey';
import validateTariff from '../../../helpers/billing/validateTariff';
import { compareTariffs } from '../../../helpers/billing/tariffForDate';

/**
 * De tarieven van één deelnemer, elk met een ingangsdatum (tabblad 'Gegevens').
 *
 * Het tarief hoort bij de deelnemer, niet bij het traject (besluit Gijs,
 * 2026-10-28); zonder eigen tarief rekent de facturatie met dat van het traject.
 * Toevoegen en verwijderen, geen bewerken: een correctie is verwijderen en
 * opnieuw toevoegen, zodat een tarief nooit stil van ingangsdatum verschuift.
 * Alleen zichtbaar met 'billing.view', te wijzigen met 'billing.edit'.
 *
 * AI-CONTEXT: See docs/features/facturatie-per-deelnemer.md
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 * AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
 *
 * @param {object} props
 * @param {string} props.personaId - `ID` van de deelnemer
 * @param {boolean} props.active - Of het tabblad openstaat; alleen dan wordt er gelezen
 * @returns {JSX.Element|null}
 */
const PersonaTariffs = ({ personaId, active }) => {
    const [auth] = useContext(Auth);
    const { has } = usePermissions();
    const canView = has('billing.view');

    const [amount, setAmount] = useState('');
    const [startDate, setStartDate] = useState('');
    const [errors, setErrors] = useState(/** @type {{amount?: string, startDate?: string}} */ ({}));
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [deleteError, setDeleteError] = useState(false);
    const [readError, setReadError] = useState(false);
    const [toDelete, setToDelete] = useState(/** @type {object|null} */ (null));

    const tariffs = useFirestoreQuery('PersonaTariffs', {
        where: [['PersonaID', '==', personaId || '']],
        enabled: canView && active,
        onError: (error) => {
            console.error('PersonaTariffs: lezen mislukt', error);
            setReadError(true);
        },
    });

    if (!canView) return null;

    const loaded = Array.isArray(tariffs);
    const rows = loaded ? [...tariffs].sort(compareTariffs) : [];
    const startDates = rows.map((t) => t.StartDate);
    const hasDuplicateDate = new Set(startDates).size !== startDates.length;

    const addTariff = async () => {
        if (saving) return;
        const result = validateTariff({ amount, startDate });
        setErrors(result.errors);
        if (!result.valid) return;

        setSaving(true);
        setSaveError(false);
        try {
            await db.collection('PersonaTariffs').add({
                ID: uuid(),
                CompagnyID: client,
                Compagny: client,
                PersonaID: personaId,
                Amount: result.amount,
                StartDate: result.startDate,
                Title: `${formatEuro(result.amount)} vanaf ${formatDateKey(result.startDate)}`,
                // Aanmaakmoment als getal: breekt een gelijkspel op ingangsdatum, en
                // is meteen bekend (serverTimestamp is vlak na aanmaken nog null).
                Created: Date.now(),
                CreatedBy: auth?.ID || '',
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                Deleted: false,
            });
            setAmount('');
            setStartDate('');
        } catch (error) {
            console.error('PersonaTariffs: opslaan mislukt', error);
            setSaveError(true);
        } finally {
            setSaving(false);
        }
    };

    const deleteTariff = async () => {
        const tariff = toDelete;
        setToDelete(null);
        setDeleteError(false);
        try {
            await softDeleteDoc({
                collection: 'PersonaTariffs',
                docid: tariff.docid,
                authId: auth?.ID,
                title: tariff.Title || `Tarief vanaf ${formatDateKey(tariff.StartDate)}`,
            });
        } catch (error) {
            console.error('PersonaTariffs: verwijderen mislukt', error);
            setDeleteError(true);
        }
    };

    return (
        // id: doel van de link 'Tarief toevoegen' op de pagina Facturatie (skipFix).
        <div className="dashboard-container billing-tariffs" id="persona-tarief">
            <h3>Tarief</h3>
            <ReadOnlyGuard edit="billing.edit">
                {readError ? (
                    <p className="billing-tariffs__error" role="alert">De tarieven zijn niet te lezen. Herlaad de pagina of vraag een beheerder.</p>
                ) : !loaded ? (
                    <p className="status-config-loading">Tarieven worden geladen…</p>
                ) : rows.length === 0 ? (
                    <p className="billing-tariffs__empty">Nog geen eigen tarief.</p>
                ) : (
                    <>
                        <Table
                            columns={[
                                { key: 'start', label: 'INGANGSDATUM', searchable: false },
                                { key: 'amount', label: 'BEDRAG', searchable: false },
                                { key: 'delete', label: '', searchable: false },
                            ]}
                            items={rows}
                            getItemKey={(t) => t.docid}
                            showSearch={false}
                            renderRow={(t) => (
                                <>
                                    <TableCell>{formatDateKey(t.StartDate)}</TableCell>
                                    <TableCell>{formatEuro(t.Amount)}</TableCell>
                                    <TableCell>
                                        <button
                                            type="button"
                                            className="billing-tariffs__delete"
                                            aria-label={`Tarief van ${formatDateKey(t.StartDate)} verwijderen`}
                                            onClick={() => setToDelete(t)}
                                        >
                                            <DeleteOutlinedIcon />
                                        </button>
                                    </TableCell>
                                </>
                            )}
                        />
                        {hasDuplicateDate && (
                            <p className="billing-tariffs__warning">Twee tarieven hebben dezelfde ingangsdatum. Het laatst toegevoegde telt; verwijder het andere.</p>
                        )}
                    </>
                )}

                <div className="billing-tariffs__form">
                    <label className="billing-tariffs__field">
                        <span>Ingangsdatum</span>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        {errors.startDate && <span className="billing-tariffs__field-error">{errors.startDate}</span>}
                    </label>
                    <label className="billing-tariffs__field">
                        <span>Bedrag</span>
                        <input type="text" inputMode="decimal" placeholder="0,00" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        {errors.amount && <span className="billing-tariffs__field-error">{errors.amount}</span>}
                    </label>
                    <button type="button" className="billing-tariffs__add" onClick={addTariff} disabled={saving}>
                        Tarief toevoegen
                    </button>
                </div>
                {saveError && <p className="billing-tariffs__error" role="alert">Niet opgeslagen. Probeer het opnieuw.</p>}
                {deleteError && <p className="billing-tariffs__error" role="alert">Niet verwijderd. Probeer het opnieuw.</p>}
            </ReadOnlyGuard>

            <DeleteModal
                deleteModal={!!toDelete}
                setDeleteModal={(open) => { if (!open) setToDelete(null); }}
                deleteItem={deleteTariff}
                deleteName={toDelete ? `het tarief van ${formatEuro(toDelete.Amount)} vanaf ${formatDateKey(toDelete.StartDate)}` : ''}
                softDelete
            />
        </div>
    );
};

export default PersonaTariffs;
